import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Package, Plus } from 'lucide-react';
import StepSection from './StepSection';
import Paginate from 'components/_UI/Paginate';
import NotResultDashBoard from 'components/commons/NotResultDashBoard';
import Skeleton from 'components/_UI/Skeleton';
import currency from 'helpers/currency';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

/**
 * Seletor de produtos já cadastrados no catálogo do produtor (aba "Geral"),
 * pra adicionar ao evento sem digitar tudo de novo. Lista e paginação já
 * chegam prontas via props — quem busca é MainContent.js, que alimenta o
 * mesmo catálogo pro step de ingressos.
 *
 * "Adicionar" empurra o item escolhido pra "Criar do zero" já preenchido
 * (mesma mecânica de ProductSuggestions.js) — daqui pra frente é só mais um
 * rascunho comum, editável e removível.
 */
export default function ProductCatalogPicker({
    products,
    pagination,
    loading = false,
    onPaginate = () => false,
    existingNames = [],
    pickedTemplateIds = [],
    onAccept = () => false,
}) {
    const takenNames = useMemo(
        () => new Set(existingNames.filter(Boolean).map(name => name.trim().toLowerCase())),
        [existingNames]
    );

    const visibleProducts = useMemo(() => {
        if (!Array.isArray(products)) return [];

        return products.filter(item => (
            !pickedTemplateIds.includes(item.id) && !takenNames.has(item.name?.trim().toLowerCase())
        ));
    }, [products, pickedTemplateIds, takenNames]);

    return (
        <StepSection
            id='product-catalog'
            icon={Package}
            title='Do seu catálogo'
            hint='Produtos que você já cadastrou na aba Geral. Escolha um pra vender também neste evento — nome, preço e foto vêm prontos, e dá pra ajustar tudo antes de salvar.'
            count={pagination?.totalItems}
        >
            {products === undefined && (
                <CardsGrid aria-hidden='true'>
                    {[0, 1, 2].map(index => (
                        <SkeletonCard key={index}>
                            <Skeleton width='100%' height='110px' />
                            <Skeleton width='70%' height='16px' style={{ marginTop: '12px' }} />
                            <Skeleton width='40%' height='14px' style={{ marginTop: '8px' }} />
                        </SkeletonCard>
                    ))}
                </CardsGrid>
            )}

            {products === false && (
                <NotResultDashBoard
                    minimal
                    text='Não foi possível carregar seu catálogo agora. Tente novamente em alguns instantes.'
                    textButton='Tentar de novo'
                    onClick={() => onPaginate(1)}
                />
            )}

            {Array.isArray(products) && products.length === 0 && (
                <NotResultDashBoard
                    ilustration='stock'
                    text='Você ainda não tem produtos no catálogo. Crie um na aba Geral ou direto aqui embaixo, em "Criar do zero".'
                />
            )}

            {Array.isArray(products) && products.length > 0 && !visibleProducts.length && (
                <NotResultDashBoard
                    minimal
                    text='Todos os produtos desta página já foram adicionados a este evento.'
                />
            )}

            {!!visibleProducts.length && (
                <Grid loading={loading} aria-busy={loading}>
                    {visibleProducts.map(item => (
                        <TemplateCard key={item.id}>
                            <Thumb src={item.image || '/assets/images/placeholder-products.png'} alt='' />
                            <TemplateBody>
                                <TemplateName title={item.name}>{item.name}</TemplateName>
                                <TemplatePrice>{currency.format(parseFloat(item.price))}</TemplatePrice>

                                <AddButton
                                    type='button'
                                    onClick={() => onAccept(item)}
                                    aria-label={`Adicionar ${item.name} ao evento`}
                                >
                                    <Plus size={15} aria-hidden='true' />
                                    Adicionar
                                </AddButton>
                            </TemplateBody>
                        </TemplateCard>
                    ))}
                </Grid>
            )}

            {pagination?.lastPage > 1 && (
                <Paginate
                    perPage={pagination.perPage}
                    totalItems={pagination.totalItems}
                    lastPage={pagination.lastPage}
                    firstPage={1}
                    onPaginate={onPaginate}
                    currentPage={pagination.currentPage}
                    marginTop='16px'
                />
            )}
        </StepSection>
    );
}

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const Grid = styled(CardsGrid).withConfig({
    shouldForwardProp: (prop) => prop !== 'loading',
})`
    opacity: ${({ loading }) => loading ? 0.6 : 1};
    transition: opacity 0.15s ease;
`;

const SkeletonCard = styled.div`
    padding: 12px;
    border: 1.5px solid ${colors.night}14;
    border-radius: 14px;
    background: ${colors.neutralLight};
`;

const TemplateCard = styled.article`
    display: flex;
    flex-direction: column;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 14px;
    background: ${colors.neutralLight};
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

    &:hover {
        border-color: ${panelAccentAlpha(colors.occasionalPurple, '55')};
        box-shadow: 0 4px 14px ${panelAccentAlpha(colors.occasionalPurple, '14')};
        transform: translateY(-2px);
    }
`;

const Thumb = styled.img`
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    flex-shrink: 0;
`;

const TemplateBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 12px 14px 14px;
    gap: 4px;
`;

const TemplateName = styled.h5`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TemplatePrice = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
`;

const AddButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    width: 100%;
    margin-top: 8px;
    padding: 9px 14px;
    border: 1.5px solid ${panelAccent(colors.occasionalPurple)};
    border-radius: 10px;
    background: transparent;
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.occasionalPurple)};
        color: ${colors.neutralLight};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;
